import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@capsico/ui';
import type { AddressView } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { addressLine, addressTitle, useAddresses } from '../hooks/useAddresses';
import {
  Button,
  Card,
  EmptyState,
  ListSkeleton,
  Notice,
  PageHeader,
  Sheet,
} from '../components/ui';

/** Saved addresses (pattern AD): list with Default chip, Edit, Make default, Remove (confirm). */
export function AddressesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const names = { home: t('address.home'), work: t('address.work'), other: t('address.other') };
  const list = useAddresses(true, { deleted: t('address.deleted') });
  const [removing, setRemoving] = useState<AddressView | null>(null);

  return (
    <div className="min-h-dvh bg-tile pb-28">
      <PageHeader
        title={t('account.addresses')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
      />
      <div className="flex flex-col gap-3 px-4 pt-5">
        {list.loading ? (
          <ListSkeleton rows={3} />
        ) : list.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={list.retry}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : list.addresses.length === 0 ? (
          <EmptyState icon="home" title={t('address.none')}>
            {t('address.noneBody')}
          </EmptyState>
        ) : (
          list.addresses.map((address) => (
            <Card key={address.id}>
              <div className="flex items-start gap-3">
                <Icon
                  name={
                    address.label === 'work' ? 'store' : address.label === 'home' ? 'home' : 'pin'
                  }
                  size={18}
                />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                    {addressTitle(address, names)}
                    {address.isDefault && (
                      <span className="rounded bg-brand-soft px-1.5 text-micro font-semibold text-brand-ink">
                        {t('address.default')}
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-2">{addressLine(address)}</p>
                  {!address.serviceable && (
                    <p className="mt-1 text-caption text-negative">{t('address.notServed')}</p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-sm font-semibold">
                <button
                  type="button"
                  onClick={() => navigate(`/address/${address.id}/edit`)}
                  className="text-brand-ink"
                >
                  {t('common.edit')}
                </button>
                {!address.isDefault && (
                  <button
                    type="button"
                    onClick={() => list.makeDefault(address.id)}
                    className="text-brand-ink"
                  >
                    {t('address.setDefault')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setRemoving(address)}
                  className="text-negative"
                >
                  {t('common.delete')}
                </button>
              </div>
            </Card>
          ))
        )}
      </div>
      <div className="fixed inset-x-0 bottom-0 z-30 mx-auto w-full max-w-120 bg-surface px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)] shadow-[inset_0_1px_0_var(--color-hair)]">
        <Button full onClick={() => navigate('/address/new')}>
          {t('location.addAddress')}
        </Button>
      </div>
      {removing && (
        <Sheet
          title={t('address.deleteConfirm')}
          onClose={() => setRemoving(null)}
          footer={
            <div className="flex gap-3">
              <Button tone="outline" full onClick={() => setRemoving(null)}>
                {t('common.keep')}
              </Button>
              <Button
                tone="danger"
                full
                onClick={() => {
                  list.remove(removing.id);
                  setRemoving(null);
                }}
              >
                {t('common.delete')}
              </Button>
            </div>
          }
        >
          <p className="text-sm text-ink-2">{addressLine(removing)}</p>
        </Sheet>
      )}
    </div>
  );
}
